/**
 * SENTRY CONFIGURATION
 * 
 * Error tracking y performance monitoring para producción.
 * 
 * Setup:
 * 1. Crear cuenta en sentry.io
 * 2. Crear proyecto React
 * 3. Copiar DSN
 * 4. Agregar a .env.production:
 *    VITE_SENTRY_DSN=https://xxx@sentry.io/xxx
 *    VITE_SENTRY_AUTH_TOKEN=xxx (para source maps)
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.MODE; // 'development' | 'production'
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '2.0.0';

/**
 * Inicializar Sentry
 * Solo se ejecuta en producción si hay DSN configurado
 */
export function initSentry() {
  // Temporalmente deshabilitado para debug
  console.log('📊 Sentry: Disabled for debug');
  return;
  
  // Solo inicializar en producción y si hay DSN
  if (ENVIRONMENT !== 'production' || !SENTRY_DSN) {
    console.log('📊 Sentry: Not initialized (dev mode or no DSN)');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    
    // Identificación
    environment: ENVIRONMENT,
    release: `appmor@${APP_VERSION}`,
    
    // Performance Monitoring
    integrations: [
      // Browser profiling
      Sentry.browserTracingIntegration({
        // Trace HTTP requests
        traceFetch: true,
        traceXHR: true,
        
        // Trace React component updates
        enableLongTask: true,
      }),
      
      // Replay sessions cuando hay errores
      Sentry.replayIntegration({
        // Solo grabar cuando hay error
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Performance Monitoring: 10% de transacciones
    tracesSampleRate: 0.1,
    
    // Session Replay: 10% de sesiones normales
    replaysSessionSampleRate: 0.1,
    
    // Session Replay: 100% cuando hay error
    replaysOnErrorSampleRate: 1.0,
    
    // Filtrar errores conocidos/no importantes
    beforeSend(event, hint) {
      const error = hint.originalException;
      
      // Ignorar errores conocidos de navegador
      if (error && error instanceof Error) {
        // ResizeObserver loop error (benigno)
        if (error.message.includes('ResizeObserver loop')) {
          return null;
        }
        
        // Script loading errors de CDN
        if (error.message.includes('Loading chunk') || 
            error.message.includes('Loading CSS chunk')) {
          return null;
        }
      }
      
      // Ignorar eventos sin error
      if (!event.exception && !event.message) {
        return null;
      }
      
      return event;
    },
    
    // Configuración de breadcrumbs
    maxBreadcrumbs: 50,
    
    // Capturar console.error automáticamente
    attachStacktrace: true,
    
    // Tags por defecto
    initialScope: {
      tags: {
        app: 'appmor',
        platform: 'web',
      },
    },
  });

  console.log('📊 Sentry: Initialized successfully');
}

/**
 * Capturar error manualmente
 */
export function captureError(error: Error, context?: Record<string, any>) {
  if (ENVIRONMENT === 'development') {
    console.error('📊 Sentry (dev):', error, context);
    return;
  }
  
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capturar mensaje/warning
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  if (ENVIRONMENT === 'development') {
    console.log(`📊 Sentry (dev) [${level}]:`, message);
    return;
  }
  
  Sentry.captureMessage(message, level);
}

/**
 * Agregar contexto de usuario
 */
export function setUserContext(userId: string, email?: string) {
  Sentry.setUser({
    id: userId,
    email,
  });
}

/**
 * Limpiar contexto de usuario (al hacer logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Agregar breadcrumb custom
 */
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
    timestamp: Date.now() / 1000,
  });
}

/**
 * Medir performance de operación
 */
export async function measurePerformance<T>(
  name: string,
  operation: () => T | Promise<T>
): Promise<T> {
  const startTime = performance.now();
  
  try {
    const result = await Promise.resolve(operation());
    const duration = performance.now() - startTime;
    
    // Log para debugging en dev
    if (import.meta.env.MODE === 'development') {
      console.log(`⏱️  ${name}: ${duration.toFixed(2)}ms`);
    }
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    captureError(error as Error, {
      operation: name,
      duration,
    });
    throw error;
  }
}

/**
 * React Error Boundary con Sentry
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Types export
export type { User } from '@sentry/react';

